import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LoadingState } from '@cinesend/ui-kit'
import FieldValue from '../field_value'
import SettingsCard from '../settings_card'
import { AUDIO_TRACK_NAMES, MEDIA_FIELD_NAMES, TEXT_TRACK_NAMES, fieldOrderIn, matchesFieldName } from './field_groups'

const MediaFields = ({ asset, cycleID }) => {
  const queryClient = useQueryClient()
  const standardKey = [`field-values`, `?assetID=${asset.id}`, `&cycleID=${cycleID}`, `&isStandard=true`]
  const commonKey = [`field-values`, `?assetID=${asset.id}`, `&cycleID=${cycleID}`]
  const { data: standardFields, isLoading: isStandardLoading } = useQuery(standardKey)
  const { data: commonFields, isLoading: isCommonLoading } = useQuery(commonKey)

  const refreshValues = () => {
    queryClient.invalidateQueries(standardKey)
    queryClient.invalidateQueries(commonKey)
  }

  if (isStandardLoading || isCommonLoading) {
    return (
      <SettingsCard title='Media'>
        <LoadingState className='justify-center' />
      </SettingsCard>
    )
  }

  const selected = []
  const seen = new Set()
  for (const [fields, cacheKey] of [
    [standardFields, standardKey],
    [commonFields, commonKey],
  ]) {
    for (const field of fields ?? []) {
      const isMediaName = matchesFieldName(MEDIA_FIELD_NAMES, field.name)
      const isImage = field.field_type === 'image'
      if (!isMediaName && !isImage) continue
      if (seen.has(field.id)) continue
      seen.add(field.id)
      selected.push({ field, cacheKey })
    }
  }
  selected.sort((a, b) => fieldOrderIn(MEDIA_FIELD_NAMES, a.field.name) - fieldOrderIn(MEDIA_FIELD_NAMES, b.field.name))

  if (selected.length === 0) return null

  const audioTracks = selected.filter(({ field }) => matchesFieldName(AUDIO_TRACK_NAMES, field.name))
  const textTracks = selected.filter(({ field }) => matchesFieldName(TEXT_TRACK_NAMES, field.name))
  const other = selected.filter(
    ({ field }) => !matchesFieldName(AUDIO_TRACK_NAMES, field.name) && !matchesFieldName(TEXT_TRACK_NAMES, field.name)
  )

  return (
    <SettingsCard title='Media'>
      {other.length > 0 ? (
        <div className='grid gap-4 sm:grid-cols-2'>
          {other.map(({ field, cacheKey }) => (
            <div key={field.id} id={`field-${field.name?.toLowerCase()?.replaceAll(' ', '-')}`}>
              <FieldValue
                cacheKey={cacheKey}
                field={field}
                assetID={asset.id}
                cycleID={cycleID}
                refreshValues={refreshValues}
              />
            </div>
          ))}
        </div>
      ) : null}
      {audioTracks.length > 0 ? (
        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {audioTracks.map(({ field, cacheKey }) => (
            <FieldValue
              key={field.id}
              cacheKey={cacheKey}
              cycleID={cycleID}
              field={field}
              assetID={asset.id}
              refreshValues={refreshValues}
            />
          ))}
        </div>
      ) : null}
      {textTracks.length > 0 ? (
        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {textTracks.map(({ field, cacheKey }) => (
            <FieldValue
              key={field.id}
              cacheKey={cacheKey}
              cycleID={cycleID}
              field={field}
              assetID={asset.id}
              refreshValues={refreshValues}
            />
          ))}
        </div>
      ) : null}
    </SettingsCard>
  )
}

export default MediaFields
